import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { EnrichDataNodeData } from '@repo/shared-types';

const ACTIONS = [
  { value: 'scrape_vehicle_details', label: 'Scrape Vehicle Details' },
  { value: 'scrape_checkout_url', label: 'Scrape Checkout URL' },
];

interface EnrichDataConfigProps {
  data: EnrichDataNodeData;
  onChange: (data: EnrichDataNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const EnrichDataConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: EnrichDataConfigProps) => {
  const update = (patch: Partial<EnrichDataNodeData>) => onChange({ ...data, ...patch });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={data.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Node label"
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Action</label>
        <Select value={data.action} onValueChange={(v) => update({ action: v })}>
          <SelectTrigger>
            <SelectValue placeholder="Select action" />
          </SelectTrigger>
          <SelectContent>
            {ACTIONS.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Input</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.input}
            onChange={(v) => update({ input: v })}
            placeholder="{{variable}} or value"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            className="flex-1"
          />
          <VariablePicker
            onSelect={(v) => update({ input: data.input + v })}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Output Variable</label>
        <Input
          value={data.outputVariable}
          onChange={(e) => update({ outputVariable: e.target.value })}
          placeholder="e.g. vehicle_details"
        />
      </div>
    </div>
  );
};
